"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import { GoogleSignInButton } from "@/components/forms/google-sign-in-button";
import { deleteClientCookie, getClientCookie } from "@/lib/cookies/client";
import { useStickyFooterClaimed } from "@/lib/monetization/sticky-footer-claim";
import { GUEST_SAVE_PROMPT_EVENT } from "@/lib/saved-jobs";

/** Set by the /out route when `guest-apply-soft-limit` is ENABLED and a guest
 * went past the daily cap (the apply still completed). */
export const GUEST_SOFT_GATE_COOKIE = "ld_guest_soft_gate";
const SURFACE_ID = "guest_save_sheet";

type Trigger = "guest_apply_limit_return" | "guest_job_save_threshold";

const COPY = {
	id: {
		body: "Simpan lamaranmu — daftar gratis 10 detik, riwayat lamaran & pengingat otomatis.",
		saveBody:
			"Lowongan tersimpan hanya ada di perangkat ini. Daftar gratis 10 detik supaya lowongan yang kamu simpan berikutnya masuk ke akunmu dan bisa dibuka di mana saja.",
		google: "Daftar dengan Google",
		later: "Nanti saja",
	},
	en: {
		body: "Save your applications: free signup in 10 seconds, application history and automatic reminders.",
		saveBody:
			"Your saved jobs live on this device only. Sign up free in 10 seconds and the jobs you save next go to your account, on every device.",
		google: "Sign up with Google",
		later: "Maybe later",
	},
} as const;

/**
 * Dismissible inline signup sheet for guests. Opens on either:
 * - the `ld_guest_soft_gate` cookie (guest hit the daily apply cap while
 *   `guest-apply-soft-limit` is on; no flag read here: the cookie only
 *   exists when the server-side flag was enabled), or
 * - the `GUEST_SAVE_PROMPT_EVENT` window event `SaveJobButton` dispatches on
 *   a guest's third on-device save.
 * Dismissing (or starting signup) clears the cookie. Never a wall.
 */
export function GuestSaveSheet({ locale }: { locale: string }) {
	const posthog = usePostHog();
	const stickyFooterClaimed = useStickyFooterClaimed();
	const [trigger, setTrigger] = useState<Trigger | null>(null);
	const shown = useRef(false);
	const lang = locale === "en" ? "en" : "id";
	const t = COPY[lang];

	useEffect(() => {
		if (getClientCookie(GUEST_SOFT_GATE_COOKIE) === "1") {
			setTrigger("guest_apply_limit_return");
		}
		const onSavePrompt = () => {
			shown.current = false;
			setTrigger("guest_job_save_threshold");
		};
		window.addEventListener(GUEST_SAVE_PROMPT_EVENT, onSavePrompt);
		return () =>
			window.removeEventListener(GUEST_SAVE_PROMPT_EVENT, onSavePrompt);
	}, []);

	useEffect(() => {
		if (!trigger || shown.current) return;
		shown.current = true;
		posthog?.capture("gate_impression", {
			surface_id: SURFACE_ID,
			gate_type: "signup",
			trigger,
			mode: "soft",
		});
	}, [trigger, posthog]);

	if (!trigger) return null;

	const body = trigger === "guest_job_save_threshold" ? t.saveBody : t.body;

	const dismiss = () => {
		posthog?.capture("gate_cta_clicked", {
			surface_id: SURFACE_ID,
			gate_type: "signup",
			cta_label: "later",
			trigger,
		});
		deleteClientCookie(GUEST_SOFT_GATE_COOKIE);
		setTrigger(null);
	};

	return (
		<section
			aria-label={body}
			data-testid="guest-save-sheet"
			className={`fixed inset-x-4 ${stickyFooterClaimed ? "bottom-20" : "bottom-4"} z-50 mx-auto block max-w-sm rounded-xl border border-border bg-card p-4 text-card-foreground shadow-lg sm:left-auto sm:right-4`}
		>
			<div className="flex flex-col gap-3">
				<p className="text-sm font-medium">{body}</p>
				<div
					onClickCapture={() => {
						posthog?.capture("gate_cta_clicked", {
							surface_id: SURFACE_ID,
							gate_type: "signup",
							cta_label: "google",
							trigger,
						});
						deleteClientCookie(GUEST_SOFT_GATE_COOKIE);
					}}
				>
					<GoogleSignInButton
						callbackURL={`/${lang}/seeker`}
						label={t.google}
						context="signup"
					/>
				</div>
				<button
					type="button"
					onClick={dismiss}
					className="rounded-lg border border-border px-3 py-1.5 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
				>
					{t.later}
				</button>
			</div>
		</section>
	);
}
